/**
 * Utility service for muting and unmuting microphone during audio playback
 * to prevent test sounds from being picked up by the microphone.
 */

const streamStates = new WeakMap();

const MicMuter = {
    /**
     * Saves the current enabled state of all audio tracks on a MediaStream and then mutes them.
     * This allows the state to be perfectly restored later by unmuteMicrophone.
     *
     * @param {MediaStream} userMediaStream - The user's media stream object, which contains the microphone audio tracks.
     */
    muteMicrophone(userMediaStream) {
        if (!userMediaStream || typeof userMediaStream.getAudioTracks !== 'function') {
            return;
        }

        const audioTracks = userMediaStream.getAudioTracks();
        if (audioTracks.length === 0) {
            return;
        }

        // Use a Map to store track-specific states for robustness
        const trackStates = new WeakMap();
        for (const track of audioTracks) {
            trackStates.set(track, track.enabled);
            track.enabled = false;
        }

        streamStates.set(userMediaStream, trackStates);
    },

    /**
     * Restores the 'enabled' state of all audio tracks on a MediaStream to whatever they were
     * before muteMicrophone was called.
     *
     * @param {MediaStream} userMediaStream - The user's media stream object to restore.
     */
    unmuteMicrophone(userMediaStream) {
        if (!userMediaStream || !streamStates.has(userMediaStream)) {
            return;
        }

        const trackStates = streamStates.get(userMediaStream);

        if (!trackStates) {
            streamStates.delete(userMediaStream);
            return;
        }

        const currentAudioTracks = userMediaStream.getAudioTracks();

        if (currentAudioTracks.length > 0) {
            for (const track of currentAudioTracks) {
                // Only restore state for tracks we've previously stored
                if (trackStates.has(track)) {
                    track.enabled = trackStates.get(track);
                }
            }
        }

        streamStates.delete(userMediaStream);
    },
};

export default MicMuter;
